import { getDerivedPropsFromErrorProps } from "@faire/web/slate/Input/error";
import { IErrorProps } from "@faire/web/slate/Input/error";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import * as React from "react";

import { ICreateNewOption, IOption } from "../types";

import { ICustomOption, IOptionMixin } from "./types";
import { getLabel } from "./utils";

interface IDropdownState<T extends string = string> {
  dropdownId: string;
  targetRef: React.RefObject<HTMLInputElement>;
  refs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  searchValue: string | undefined;
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  highlightIndex: number;
  setHighlightIndex: React.Dispatch<React.SetStateAction<number>>;
  isCustomizingOption: boolean;
  setIsCustomizingOption: React.Dispatch<React.SetStateAction<boolean>>;
  filteredOptions: IOptionMixin<T>[];
  ariaProps: {
    "aria-label"?: string;
    "aria-labelledby"?: string;
  };
  showErrorState: boolean;
  showErrorMessage: boolean;
  errorMessage?: string;
  showMobileDropdown: boolean;
}

interface DropdownStateProps<T extends string = string> extends IErrorProps {
  id?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  filter?: boolean;
  options: IOption<T>[];
  customOption?: ICustomOption<T>;
  createNewOption?: ICreateNewOption;
  useTabletAndAboveDropdownStyle?: boolean;
  ariaLabel?: string;
  label?: string;
}

export const useDropdownState = <T extends string = string>({
  id,
  inputRef,
  filter,
  options,
  customOption,
  useTabletAndAboveDropdownStyle,
  error,
  ariaLabel,
  label,
}: DropdownStateProps<T>): IDropdownState<T> => {
  const viewport = useViewport();
  const defaultId = useUniqueId("input-");
  const dropdownId = id ?? defaultId;
  let targetRef = React.useRef<HTMLInputElement>(null);
  if (inputRef) {
    targetRef = inputRef;
  }

  const refs = React.useRef<Record<number, HTMLButtonElement | null>>({});
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState<string | undefined>(
    undefined
  );
  const [highlightIndex, setHighlightIndex] = React.useState(0);

  const [isCustomizingOption, setIsCustomizingOption] = React.useState(false);

  const filteredOptions = React.useMemo((): IOptionMixin<T>[] => {
    if ((!searchValue || !filter) && customOption === undefined) {
      return options;
    }

    const containsSearch = (option: IOption<T>) =>
      getLabel(option)
        .toLowerCase()
        .indexOf(searchValue?.toLowerCase() ?? "") !== -1;
    const filtered = options.filter(containsSearch);
    if (customOption !== undefined && customOption.value !== undefined) {
      // 2 cases:
      //  1. the search value matches the custom value
      //  2. user hasn't constructed the custom value yet
      // in both cases we want maintain it in the dropdown
      if (
        customOption.value.indexOf(searchValue?.toLowerCase() ?? "") !== -1 ||
        !customOption.hasEdited
      ) {
        filtered.push(customOption);
      }
    }
    return filtered;
  }, [searchValue, filter, customOption, options]);

  const { showErrorState, showErrorMessage, errorMessage } =
    getDerivedPropsFromErrorProps({ error });

  const labelId = label ? `${dropdownId}__label` : undefined;

  const ariaProps = React.useMemo(() => {
    if (ariaLabel) {
      return { "aria-label": ariaLabel };
    }
    if (labelId) {
      return { "aria-labelledby": labelId };
    }
    return {};
  }, [ariaLabel, labelId]);

  const showMobileDropdown =
    viewport.isMobile && !useTabletAndAboveDropdownStyle;

  return {
    dropdownId,
    targetRef,
    refs,
    open,
    setOpen,
    searchValue,
    setSearchValue,
    highlightIndex,
    setHighlightIndex,
    isCustomizingOption,
    setIsCustomizingOption,
    filteredOptions,
    showErrorState,
    showErrorMessage,
    errorMessage,
    ariaProps,
    showMobileDropdown,
  };
};
